import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Sparkle, ShoppingBag } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

const LOOKBOOK = "https://static.prod-images.emergentagent.com/jobs/062e260e-b0f9-432f-a759-5890d554bb91/images/ffca77578e4e5005225a092ee29363baf103b89774596ca6a4ca6fc426074dd4.jpeg";

// Every hotspot is a real Wigglez product on our own store — no external links.
const HOTSPOTS = [
  { match: "neon hydro hat", top: "13%", left: "52%" },
  { match: "reflective pitbull bandana", top: "34%", left: "49%" },
  { match: "scarlett 69", top: "48%", left: "34%" },
  { match: "premium tee", top: "52%", left: "53%" },
];

export default function ShoppableLookbook() {
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);

  useEffect(() => { api.get("/products").then(({ data }) => setProducts(data)).catch(() => {}); }, []);

  const find = (m) => products.find((p) => p.name.toLowerCase().includes(m));
  const productItems = HOTSPOTS.map((h) => find(h.match)).filter(Boolean);

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20" data-testid="pit-factor-lookbook">
      <div className="flex items-center gap-2 mb-2">
        <Sparkle weight="fill" size={20} className="text-primary" />
        <span className="overline text-primary">P'IT FACTOR™ · Shop the look</span>
      </div>
      <h2 className="text-3xl sm:text-4xl tracking-tight font-light mb-8">Tap the fit. Cop the drip.</h2>

      <div className="grid md:grid-cols-2 gap-10 items-start">
        <div className="relative rounded-2xl overflow-hidden border border-primary/20 neon-glow">
          <img src={LOOKBOOK} alt="Mr. Wigglez lookbook — shop the fit" className="w-full object-cover" />
          {HOTSPOTS.map((h) => {
            const p = find(h.match);
            if (!p) return null;
            return (
              <div key={h.match} className="absolute group" style={{ top: h.top, left: h.left }}>
                <button className="h-7 w-7 rounded-full bg-primary text-primary-foreground grid place-items-center pulse-ring -translate-x-1/2 -translate-y-1/2 hover:scale-110 transition-transform" data-testid={`hotspot-${p.id}`} aria-label={`Shop ${p.name}`}>
                  <Plus weight="bold" size={15} />
                </button>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute z-20 left-1/2 top-3 -translate-x-1/2 w-52 bg-popover border border-white/10 rounded-xl p-3 shadow-2xl pointer-events-none group-hover:pointer-events-auto" data-testid={`hotspot-card-${p.id}`}>
                  <div className="flex gap-3">
                    <div className="h-14 w-14 rounded-lg overflow-hidden bg-muted shrink-0"><img src={(p.images || [])[0]} alt="" className="h-full w-full object-cover" /></div>
                    <div className="min-w-0">
                      <p className="text-xs font-modern leading-tight line-clamp-2">{p.name}</p>
                      <p className="text-sm text-primary mt-1">${p.price}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => addItem(p)} className="flex-1 overline py-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors" data-testid={`hotspot-add-${p.id}`}>Add</button>
                    <Link to={`/product/${p.id}`} className="flex-1 text-center overline py-1.5 rounded-full border border-white/20 hover:border-white/40 transition-colors">View</Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div>
          <p className="text-muted-foreground leading-relaxed mb-6">
            Every piece on the model is a real Wigglez drop — the hat too. Tap a <span className="text-primary">pit‑dot</span> to see the price and add it straight to your bag. This is P'IT FACTOR™ — shopping the way it should be.
          </p>
          <div className="space-y-3">
            {productItems.map((p) => (
              <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl bg-card border border-white/5 hover:border-primary/30 transition-colors" data-testid={`lookbook-item-${p.id}`}>
                <div className="h-12 w-12 rounded-lg overflow-hidden bg-muted shrink-0"><img src={(p.images || [])[0]} alt="" className="h-full w-full object-cover" /></div>
                <Link to={`/product/${p.id}`} className="flex-1 min-w-0"><p className="text-sm font-modern truncate hover:text-primary transition-colors">{p.name}</p><p className="text-xs text-primary">${p.price}</p></Link>
                <button onClick={() => addItem(p)} className="h-9 w-9 rounded-full border border-primary/40 text-primary grid place-items-center hover:bg-primary hover:text-primary-foreground transition-colors" data-testid={`lookbook-add-${p.id}`}>
                  <Plus weight="bold" size={16} />
                </button>
              </div>
            ))}
          </div>
          <Link to="/build" className="inline-flex items-center gap-2 mt-6 bg-primary text-primary-foreground px-6 py-3 rounded-full font-modern font-medium hover:bg-primary/90 transition-colors" data-testid="lookbook-build-btn">
            <ShoppingBag weight="fill" size={18} /> Build your own fit
          </Link>
        </div>
      </div>
    </section>
  );
}
